/*
  Diamond H Ranch: hero background video
  <dh-hero-video> controls a Shopify-hosted <video>, or a YouTube/Vimeo
  iframe, behind the hero:
    - muted autoplay (browsers only allow autoplay when muted)
    - play/pause and sound on/off buttons
    - pauses when scrolled out of view, resumes when back
    - no autoplay for visitors with "reduce motion" turned on
    - optional image-only on phones (the video is never downloaded there)
*/
(() => {
  if (customElements.get('dh-hero-video')) return;

  const ORIGINS = {
    youtube: 'https://www.youtube-nocookie.com',
    vimeo: 'https://player.vimeo.com',
  };

  class DhHeroVideo extends HTMLElement {
    connectedCallback() {
      this.kind = this.dataset.type; // native | youtube | vimeo
      this.video = this.querySelector('video');
      this.iframe = this.querySelector('iframe');
      this.playBtn = this.querySelector('[data-dh-play]');
      this.muteBtn = this.querySelector('[data-dh-mute]');

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.phone = window.matchMedia('(max-width: 749px)');

      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
      this.playing = false;
      this.muted = true;
      this.started = false;   // has played at least once
      this.userPaused = false; // visitor pressed pause; don't auto-resume
      this.inView = false;

      this.onPlayClick = () => (this.playing ? this.pause(true) : this.play(true));
      this.onMuteClick = () => this.setMuted(!this.muted);
      this.onPhoneChange = () => {
        if (this.imageOnly()) this.pause(false);
        else if (this.shouldRun()) this.play(false);
      };

      this.playBtn?.addEventListener('click', this.onPlayClick);
      this.muteBtn?.addEventListener('click', this.onMuteClick);
      this.phone.addEventListener?.('change', this.onPhoneChange);

      if (this.video) {
        this.video.muted = true;
        this.onVideoPlaying = () => this.setPlaying(true);
        this.onVideoPause = () => this.setPlaying(false);
        this.video.addEventListener('playing', this.onVideoPlaying);
        this.video.addEventListener('pause', this.onVideoPause);
      }

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.inView = entry.isIntersecting;
          if (this.imageOnly()) return;
          if (this.inView) {
            if (this.shouldRun()) this.play(false);
          } else if (this.playing) {
            this.pause(false);
          }
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this);

      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.playBtn?.removeEventListener('click', this.onPlayClick);
      this.muteBtn?.removeEventListener('click', this.onMuteClick);
      this.phone?.removeEventListener?.('change', this.onPhoneChange);
      if (this.video) {
        this.video.removeEventListener('playing', this.onVideoPlaying);
        this.video.removeEventListener('pause', this.onVideoPause);
      }
    }

    imageOnly() {
      return this.hasAttribute('data-mobile-image') && this.phone.matches;
    }

    shouldRun() {
      return this.inView && !this.userPaused && (this.autoplay || this.started);
    }

    play(fromUser) {
      if (fromUser) this.userPaused = false;
      if (this.imageOnly()) return;

      if (this.video) {
        if (this.video.preload === 'none') this.video.preload = 'auto';
        const attempt = this.video.play();
        if (attempt && attempt.catch) attempt.catch(() => this.setPlaying(false));
        return;
      }

      if (this.iframe) {
        if (!this.iframe.src) {
          let src = this.iframe.dataset.src;
          if (this.kind === 'youtube') src += '&origin=' + encodeURIComponent(location.origin);
          this.iframe.addEventListener(
            'load',
            () => {
              this.command(this.muted ? 'mute' : 'unmute');
              // give the player a moment to show its first frame before fading the image out
              setTimeout(() => this.setPlaying(true), 700);
            },
            { once: true }
          );
          this.iframe.src = src;
        } else {
          this.command('play');
          this.setPlaying(true);
        }
      }
    }

    pause(fromUser) {
      if (fromUser) this.userPaused = true;
      if (this.video) {
        this.video.pause();
      } else if (this.iframe?.src) {
        this.command('pause');
        this.setPlaying(false);
      }
    }

    setMuted(muted) {
      this.muted = muted;
      if (this.video) {
        this.video.muted = muted;
        if (!muted) this.video.volume = 1;
      } else {
        this.command(muted ? 'mute' : 'unmute');
      }
      // turning sound on implies the visitor wants to hear it
      if (!muted && !this.playing) this.play(true);
      this.render();
    }

    setPlaying(playing) {
      this.playing = playing;
      if (playing) {
        this.started = true;
        this.setAttribute('data-started', '');
      }
      this.render();
    }

    command(action) {
      const win = this.iframe?.contentWindow;
      if (!win || !this.iframe.src) return;

      if (this.kind === 'youtube') {
        const send = (func, args = []) =>
          win.postMessage(JSON.stringify({ event: 'command', func, args }), ORIGINS.youtube);
        if (action === 'play') send('playVideo');
        if (action === 'pause') send('pauseVideo');
        if (action === 'mute') send('mute');
        if (action === 'unmute') { send('unMute'); send('setVolume', [100]); }
      }

      if (this.kind === 'vimeo') {
        const send = (method, value) =>
          win.postMessage(JSON.stringify(value === undefined ? { method } : { method, value }), ORIGINS.vimeo);
        if (action === 'play') send('play');
        if (action === 'pause') send('pause');
        if (action === 'mute') send('setMuted', true);
        if (action === 'unmute') { send('setMuted', false); send('setVolume', 1); }
      }
    }

    render() {
      this.toggleAttribute('data-playing', this.playing);
      this.toggleAttribute('data-muted', this.muted);
      this.playBtn?.setAttribute('aria-label', this.playing ? 'Pause video' : 'Play video');
      this.muteBtn?.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
    }
  }

  customElements.define('dh-hero-video', DhHeroVideo);
})();
